/**
 * CategoryTiles — "Browse by category" grid (X.6.5B; TypeScript per UX-13
 * S1, markup unchanged). Restores the category-browse affordance that the
 * compact SearchBar dropped in X.6.5A, now as a body section (prototype
 * README §5).
 *
 * Auto-fill grid of the 9 canonical categories: the IdentityChip primitive
 * + Playfair name + "{count} resources". Selecting a tile sets the category
 * filter (synced with the dropdown via the same setCategoryFilter). Hidden in
 * People view by the caller. Per-category tints come from
 * CATEGORY_TILE_COLORS (identity data), read inside IdentityChip; this file
 * carries no raw hexes.
 *
 * Compact: one line per tile, about half the
 * old height: the category's icon in a small tinted circle, the name, the
 * count quiet at the right. A hairline card with no shadow (the working
 * register's lighter treatment), in columns the tiles fill (see the grid).
 * The caller shows the grid only while browsing (no search, no
 * filter on), so it folds away once a choice is made.
 *
 * Exports CategoryChip (= IdentityChip) for reuse by ResourceCard until the
 * S2 card port adopts the primitive directly.
 */

import { useMemo, type ReactNode } from 'react'
import { Building2, type LucideIcon } from 'lucide-react'
import { CATEGORY_SLUG_TO_DISPLAY, CATEGORY_TILE_COLORS } from '@/lib/uiCategoryLabels'
import { CATEGORY_ICON } from '@/lib/categoryIcons'
import { IdentityChip as CategoryChip } from '@/components/primitives/IdentityChip'
import { PAGE_WIDTH_CLASSES, PAGE_GUTTER } from '@/components/primitives/PageShell'
import type { ProviderForSearch } from '@/lib/types'

export { CategoryChip }

// Canonical order: the 9 display names, by slug map insertion order.
const DISPLAY_ORDER: string[] = Object.values(CATEGORY_SLUG_TO_DISPLAY)
const DISPLAY_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_SLUG_TO_DISPLAY).map(([slug, display]) => [display, slug]),
)

export interface CategoryTilesProps {
  resources: ProviderForSearch[] | null | undefined
  activeCategorySlugs?: string[]
  onSelect: (slug: string) => void
  /** False while the resources are loading or failed: a count would read
   *  as zero. The tiles still show and still filter. */
  showCounts?: boolean
}

export function CategoryTiles({ resources, activeCategorySlugs = [], onSelect, showCounts = true }: CategoryTilesProps): ReactNode {
  const counts = useMemo(() => {
    const m: Record<string, number> = {}
    for (const r of resources ?? []) {
      m[r.category_slug] = (m[r.category_slug] ?? 0) + 1
    }
    return m
  }, [resources])

  return (
    <section className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} pt-4`} data-testid="category-tiles">
      {/* The body eyebrow (gold, as on the Hub's "Jump back in"), kept
          small above the compact grid. */}
      <div className="mb-2 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-warm-400">
        <span aria-hidden="true" className="h-px w-[18px] bg-warm-400" />
        Browse by category
      </div>
      {/* Every row full, never a tile alone on the last one: the 9 tiles in
          3 columns from sm (3 full rows), one column below it, where 2 would
          leave the ninth alone and 3 is too narrow for the names. A change
          to the tile count needs column counts that divide it. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {DISPLAY_ORDER.map((display) => {
          const slug = DISPLAY_TO_SLUG[display]
          const active = activeCategorySlugs.includes(slug)
          const Icon: LucideIcon = CATEGORY_ICON[display] ?? Building2
          const tint = CATEGORY_TILE_COLORS[display] ?? { bg: 'var(--color-sand)', fg: 'var(--color-sage-600)' }
          return (
            <button
              key={slug}
              type="button"
              onClick={() => onSelect(slug)}
              data-testid={`category-tile-${slug}`}
              aria-pressed={active}
              className={
                'flex min-h-11 items-center gap-2.5 rounded-base border px-2 py-1.5 text-left transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                (active ? 'border-sage-800 bg-sage-800/[6%]' : 'border-sage-800/[10%] bg-paper hover:border-sage-400')
              }
            >
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                style={{ background: tint.bg, color: tint.fg }}
              >
                <Icon size={15} />
              </span>
              <span className="min-w-0 flex-1 text-[13px] font-semibold leading-tight text-sage-800">
                {display}
              </span>
              {showCounts && (
                <span data-testid="category-tile-count" className="shrink-0 text-xs font-semibold tabular-nums text-sage-400">
                  {(counts[slug] ?? 0).toLocaleString()}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </section>
  )
}
